import {
  forwardRef,
  HTMLAttributes,
  useImperativeHandle,
  useState
} from 'react';

import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Employee } from './EmployeeGrid/types';

import { createFilterOptions } from 'utils/filterOptions';
import { sortAlphabeticallyBy } from 'utils/misc';

import Autocomplete, {
  AutocompleteRenderOptionState
} from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popper from '@mui/material/Popper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';

import { MuiAutocompleteOption } from 'components/shared';

export type RefProps = {
  clearSearchFilter: () => void;
};

export type FilterOption = {
  sortValue: number;
  groupBy: string;
  label: string;
  searchValue: string;
  type: 'name' | 'function' | 'planunit';
};

const filterOptions = createFilterOptions(
  'label',
  ['searchValue'],
  'sortValue'
);

const renderOption = (
  props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
  option: FilterOption,
  { selected, inputValue }: AutocompleteRenderOptionState
) => (
  <MuiAutocompleteOption
    {...props}
    key={`${props.id}_${option.label}`}
    selected={selected}
    label={option.label}
    inputValue={inputValue}
  />
);

type Props = {
  employees: Employee[];
  showOnlyPlanned: boolean;
  onShowOnlyPlanned: () => void;
  onFilterOptionSelected: (o: FilterOption | null) => void;
};

export default forwardRef(function HeaderFilters(
  {
    employees,
    showOnlyPlanned,
    onShowOnlyPlanned,
    onFilterOptionSelected
  }: Props,
  ref
) {
  const { t } = useTranslation('common');
  const [selectedOption, setSelectedOption] = useState<FilterOption | null>(
    null
  );

  const employeeOptions: FilterOption[] = sortAlphabeticallyBy(
    uniqBy(employees, 'id').map(
      ({
        nameInformal,
        nameCompound,
        nameCompoundSurname,
        nameSurnameCompound
      }): FilterOption => ({
        sortValue: 1,
        groupBy: t('byName').toUpperCase(),
        label: nameInformal,
        searchValue: `${nameCompound} ${nameCompoundSurname} ${nameSurnameCompound}`,
        type: 'name'
      })
    ),
    'label'
  );

  const functionOptions: FilterOption[] = sortAlphabeticallyBy(
    uniqBy(
      employees.filter((e) => e.benefit.function?.name),
      'benefit.function.name'
    ).map(
      ({ benefit }): FilterOption => ({
        sortValue: 2,
        groupBy: t('byFunction').toUpperCase(),
        label: benefit.function?.name || '*',
        searchValue: benefit.function?.name || '',
        type: 'function'
      })
    ),
    'label'
  );

  const planunitOptions: FilterOption[] = sortAlphabeticallyBy(
    uniqBy(employees, 'benefit.costCenter.id').map(
      ({ benefit }): FilterOption => ({
        sortValue: 3,
        groupBy: t('byPlanunit').toUpperCase(),
        label: benefit.costCenter?.name || '*',
        searchValue: benefit.costCenter?.name || '',
        type: 'planunit'
      })
    ),
    'label'
  );

  const options: FilterOption[] = (
    employeeOptions.length > 1 ? employeeOptions : []
  )
    .concat(functionOptions.length > 1 ? functionOptions : [])
    .concat(planunitOptions.length > 1 ? planunitOptions : []);

  useImperativeHandle(
    ref,
    () =>
      ({
        clearSearchFilter() {
          setSelectedOption(null);
        }
      }) as RefProps
  );

  return (
    <>
      <FormControlLabel
        control={<Switch checked={showOnlyPlanned} />}
        label={`${t('plannedEmployees')}`}
        onChange={onShowOnlyPlanned}
      />
      <Autocomplete
        value={selectedOption}
        options={options}
        getOptionLabel={(o) => o.label}
        filterOptions={filterOptions}
        renderInput={(params) => (
          <TextField {...params} label={t('filterEmployees')} />
        )}
        renderOption={renderOption}
        onChange={(e, o) => {
          setSelectedOption(o);
          onFilterOptionSelected(o);
        }}
        autoHighlight
        isOptionEqualToValue={(a, b) =>
          a.groupBy === b.groupBy && a.label === b.label
        }
        PopperComponent={(props) => (
          <Popper
            {...props}
            style={{ width: 'auto' }}
            placement="bottom-start"
          />
        )}
        groupBy={(o) => o.groupBy}
        fullWidth
        disabled={options.length < 1}
      />
    </>
  );
});
